import type { InputHTMLAttributes, ReactNode, Ref } from "react";
import { classNames } from "./internal/class-names";
import { resolveControlAria, useFieldBinding } from "./field";

type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "aria-describedby" | "aria-invalid" | "type"
>;

export interface TextInputProps extends NativeInputProps {
  readonly type?: "email" | "password" | "search" | "tel" | "text" | "url" | undefined;
  readonly describedBy?: string | undefined;
  readonly invalid?: boolean | undefined;
  readonly ref?: Ref<HTMLInputElement> | undefined;
}

/** Single-line text control. Inherits label, hint, error and required state from an enclosing `Field`. */
export function TextInput({
  className,
  describedBy,
  invalid,
  type = "text",
  ...rest
}: TextInputProps): ReactNode {
  const aria = resolveControlAria(useFieldBinding(), {
    describedBy,
    disabled: rest.disabled,
    id: rest.id,
    invalid,
    required: rest.required,
  });

  return (
    <input
      {...rest}
      aria-describedby={aria.describedBy}
      aria-invalid={aria.invalid}
      className={classNames("thw-control", "thw-input", className)}
      disabled={aria.disabled}
      id={aria.id}
      required={aria.required}
      type={type}
    />
  );
}
